import { useEffect, useState } from "react";

/**
 * MDTBoot — breve animazione di boot "BIOS Toughbook".
 * Mostrata SOLO al primo ingresso di dashboard nella sessione (marker in
 * sessionStorage). Dura max 2.5 secondi ed è saltabile in QUALSIASI modo
 * (click, tasto, touch, wheel). Evita che l'utente veda una schermata verde
 * bloccante dopo login/registrazione.
 */
const BOOT_LINES = [
  { t: 0,    text: "PANASONIC TOUGHBOOK · CF-33-LSSD · BIOS v7.32" },
  { t: 250,  text: "> POST · CPU/RAM/GPU · [ OK ]" },
  { t: 500,  text: "> STORAGE · SSD-256GB · [ OK ]" },
  { t: 750,  text: "> RADIO · GPS · TLS 1.3 · [ OK ]" },
  { t: 1050, text: "> LSSD NETWORK · CONNECTED" },
  { t: 1400, text: "> READY · WELCOME OPERATOR" },
];

const MAX_DURATION_MS = 2200;
const SESSION_MARKER = "lssd_boot_seen";

export default function MDTBoot({ onDone }) {
  // Non ripartire il boot se la sessione l'ha già visto (es. dopo login o refresh).
  const initialSeen = (() => {
    try { return sessionStorage.getItem(SESSION_MARKER) === "1"; } catch { return false; }
  })();
  const [visible, setVisible] = useState(!initialSeen);
  const [visibleLines, setVisibleLines] = useState(0);

  const finish = () => {
    try { sessionStorage.setItem(SESSION_MARKER, "1"); } catch (err) { console.debug("[mdt-boot] sessionStorage set fail", err); }
    setVisible(false);
    onDone?.();
  };

  useEffect(() => {
    if (!visible) return undefined;
    const timers = BOOT_LINES.map((l, i) =>
      setTimeout(() => setVisibleLines(i + 1), l.t)
    );
    const closer = setTimeout(() => finish(), MAX_DURATION_MS);
    return () => { timers.forEach(clearTimeout); clearTimeout(closer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const skip = () => finish();
    window.addEventListener("keydown", skip);
    window.addEventListener("click", skip);
    window.addEventListener("touchstart", skip, { passive: true });
    window.addEventListener("wheel", skip, { passive: true });
    return () => {
      window.removeEventListener("keydown", skip);
      window.removeEventListener("click", skip);
      window.removeEventListener("touchstart", skip);
      window.removeEventListener("wheel", skip);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="mdt-boot" data-testid="mdt-boot" role="status" aria-live="polite" onClick={finish}>
      <div className="mdt-boot-inner">
        <div className="mdt-boot-header">
          <div className="mdt-boot-logo">LS</div>
          <div>
            <div className="mdt-boot-brand">PANASONIC · TOUGHBOOK · CF-33-LSSD</div>
            <div className="mdt-boot-sub">LSSD Mobile Data Terminal · Field Unit</div>
          </div>
        </div>
        <div className="mdt-boot-log">
          {BOOT_LINES.slice(0, visibleLines).map((l, i) => (
            <div key={i} className="mdt-boot-line">
              {l.text.startsWith(">") ? <span className="mdt-boot-arrow">&gt;</span> : null}
              <span>{l.text.replace(/^>\s*/, "")}</span>
            </div>
          ))}
          {visibleLines < BOOT_LINES.length && (
            <div className="mdt-boot-line">
              <span className="mdt-boot-arrow">&gt;</span>
              <span className="mdt-boot-cursor">_</span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); finish(); }}
          data-testid="mdt-boot-skip"
          className="mdt-boot-skip-btn"
          aria-label="Salta boot"
        >
          TOCCA PER ENTRARE →
        </button>
      </div>
    </div>
  );
}
